import React, { Component } from 'react';
import { connect } from 'react-redux';
import FadeLoader from 'react-spinners/FadeLoader';
import { getAllRounds, getGlobalScriptsForCustomer } from '../../actions/CompetitionActions';
import { loadLocale } from '../../actions/LocaleAction';
import { getGlobalCode, getUrlWord, injectGlobalCode } from '../../util/helpers/Helpers';
import {
    initReactI18next,
    withTranslation,
} from 'react-i18next';
import i18n from 'i18next';
import $ from 'jquery';

/**
 * AppShell
 *
 * Wraps every route and guarantees:
 *
 *  1. getAllRounds (→ getActiveCompetitions) fires on every direct-URL entry,
 *     populating state.default.competition for ALL child components.
 *
 *  2. Background image / color from competition data is applied to body/#root
 *     using the exact same responsive logic as App.js resizeContainer.
 *
 *  3. A full-screen FadeLoader blocks child rendering until competition data
 *     is available — so comp is NEVER null when children render.
 *
 *  4. Locale / i18n and global code injection run once here.
 *
 * Usage in src/index.js:
 *
 *   import { withAppShell } from './views/components/AppShell';
 *   const ShellApp            = withAppShell(App);
 *   const ShellCodeUnlockPage = withAppShell(CodeUnlockPage);
 *   // <Route path="..." component={ShellCodeUnlockPage} />
 */
class AppShell extends Component {
    constructor(props) {
        super(props);
        this.state = {
            codeInjected:     false,
            bgApplied:        false,
        };
    }

    componentDidMount() {
        const { dispatch, location, match } = this.props;
        const hash = getUrlWord(match.url, location.hash);
        if (hash) {
            dispatch(getGlobalScriptsForCustomer(hash, dispatch));
            dispatch(getAllRounds(hash, dispatch));
        }
    }

    // ── Mirror of App.js resizeContainer ─────────────────────────────────
    // Applies bgColor to #root and responsive background image to body.
    // Called once when competition data first becomes available.
    applyBackground = (data) => {
        if (!data) return;

        // Solid background color on the root element
        if (data.bgColor) {
            const root = document.getElementById('root');
            if (root) {
                root.style.backgroundColor = data.bgColor;
                root.style.minHeight = '100vh';
            }
        }

        const iw = window.innerWidth;
        let useStyleBg = '';

        // Responsive background image — same breakpoints as App.js
        if (data.desktopBg && data.desktopBg.file) {
            useStyleBg = data.desktopBg.file;
        }
        if (data.tabletBg && data.tabletBg.file && iw <= 1030 && iw >= 400) {
            if (data.id && [2268, 2301, 4954, 4555].includes(data.id)
                && data.mobileBg && iw <= 450) {
                useStyleBg = data.mobileBg.file;
            } else {
                useStyleBg = data.tabletBg.file;
            }
        } else if (data.mobileBg && data.mobileBg.file && iw < 400) {
            useStyleBg = data.mobileBg.file;
        }

        if (useStyleBg) {
            // iPhone/iPad fixed-background fix — identical to App.js
            const styles = `
                body {
                    background: url(${useStyleBg}) no-repeat center center fixed;
                }
                body:after {
                    content: "";
                    position: fixed;
                    top: 0;
                    height: 100vh;
                    left: 0;
                    right: 0;
                    z-index: -1;
                    background: url(${useStyleBg}) center center;
                    -webkit-background-size: cover;
                    -moz-background-size: cover;
                    -o-background-size: cover;
                    background-size: cover;
                }
            `;
            const styleSheet = document.createElement('style');
            styleSheet.innerText = styles;
            document.head.appendChild(styleSheet);
        }

        // CSS and font files
        if (data.cssContentUrl) {
            const link = document.createElement('link');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('type', 'text/css');
            link.setAttribute('href', data.cssContentUrl);
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        if (data.fontUrl) {
            const link = document.createElement('link');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('type', 'text/css');
            link.setAttribute('href', data.fontUrl);
            document.getElementsByTagName('head')[0].appendChild(link);
        }

        if (data.name) {
            document.title = data.name.replace(/(<([^>]+)>)/gi, ' ').trim();
        }
    };

    componentDidUpdate(prevProps) {
        const { competition, locale, dispatch, globalScript } = this.props;

        // ── Apply background + CSS once when data first arrives ──────────
        if (!prevProps.competition.dataAvailable && competition.dataAvailable) {
            this.applyBackground(competition.data);
            this.setState({ bgApplied: true });
        }

        // ── Locale init ───────────────────────────────────────────────────
        if (!prevProps.competition.data && competition.data) {
            dispatch(loadLocale(competition.data.localeId, dispatch));
        }

        if (
            locale.lang &&
            Object.keys(locale.lang).length !== 0 &&
            !prevProps.locale.lang.pick_now
        ) {
            i18n.use(initReactI18next).init({
                lang:        competition.data.localeId,
                fallbackLng: competition.data.localeId,
                debug:       false,
                ns:          ['common'],
                defaultNS:   'common',
                interpolation: { escapeValue: false },
                resources: {
                    [competition.data.localeId]: {
                        common: locale.lang,
                    },
                },
            });
        }

        // ── Global code injection ─────────────────────────────────────────
        if (
            competition.data &&
            competition.data.id > 0 &&
            !this.state.codeInjected
        ) {
            const { injectionCode } = competition.data;
            if (injectionCode) {
                const head   = injectionCode.find(ic => ic.page === 'global' && ic.place === 'head');
                const body   = injectionCode.find(ic => ic.page === 'global' && ic.place === 'body');
                const footer = injectionCode.find(ic => ic.page === 'global' && ic.place === 'footer');
                if (head   && head.code)   $('head').append(head.code);
                if (body   && body.code)   $('body').append(body.code);
                if (footer && footer.code) $('footer').append(footer.code);
            }
            this.setState({ codeInjected: true });
        }

        if (!prevProps.globalScript.globalScriptFetched && globalScript.globalScriptFetched) {
            const gsd = globalScript.globalScriptData;
            if (gsd) {
                if (gsd.globalCodeForHeader) injectGlobalCode($, 'head',   getGlobalCode(gsd.globalCodeForHeader));
                if (gsd.globalCodeForBody)   injectGlobalCode($, 'body',   getGlobalCode(gsd.globalCodeForBody));
                if (gsd.globalCodeForFooter) injectGlobalCode($, 'footer', getGlobalCode(gsd.globalCodeForFooter));
            }
        }
    }

    render() {
        const { competition, WrappedComponent, ...rest } = this.props;

        // Block all child rendering until competition data is ready.
        // Guarantees comp is non-null for Login, CodeUnlock, and every
        // other component that reads comp.defaultShape / comp.buttonColor.
        if (!competition.dataAvailable) {
            return (
                <div className="loaderCss">
                    <div className="loader">
                        <FadeLoader size={150} color="#ffffff" loading={true} />
                    </div>
                </div>
            );
        }

        return <WrappedComponent {...rest} />;
    }
}

const mapStateToProps = (state, ownProps) => {
    const things = state.default;
    return {
        ...ownProps,
        competition:  things.competition,
        comp:         things.competition.data,
        auth:         things.auth,
        locale:       things.locale,
        globalScript: things.competition.globalScript || { globalScriptFetched: false },
    };
};

const mapDispatchToProps = dispatch => ({ dispatch });

const ConnectedAppShell = connect(mapStateToProps, mapDispatchToProps)(AppShell);

/**
 * withAppShell(MyPage)
 *
 * HOC factory. Wraps any page component with AppShell so it always has
 * competition data loaded before rendering.
 *
 *   const ShellCodeUnlockPage = withAppShell(CodeUnlockPage);
 *   <Route path="/unlock" component={ShellCodeUnlockPage} />
 */
export const withAppShell = (WrappedComponent) => {
    const name = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    const WithAppShell = (routerProps) => (
        <ConnectedAppShell {...routerProps} WrappedComponent={WrappedComponent} />
    );
    WithAppShell.displayName = `withAppShell(${name})`;
    return WithAppShell;
};

export default ConnectedAppShell;
